/* iter-ab2 — Admin panel to manage Fish Audio cloned voices used by the
 * author-facing "Cloned Voice" audiobook option (AudiobookDialog.jsx).
 * Upload a short reference sample -> backend clones it via Fish Audio
 * (routes_audiobook.py: POST /admin/audiobook/fish-voices) and the voice
 * becomes selectable by every author with the audiobook_voice_cloning
 * capability.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Headphones, Upload, Loader2, Mic, Trash2, Play, Square } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader,
  AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from '@/components/ui/alert-dialog';

const API  = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const http = axios.create({ baseURL: API, withCredentials: true });

export default function AudiobookVoicesPanel() {
  const [voices, setVoices] = useState(null);
  const [title, setTitle] = useState('');
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  // ── Preview state ────────────────────────────────────────────────────────
  const [previewLoadingId, setPreviewLoadingId] = useState(null);
  const [previewPlayingId, setPreviewPlayingId] = useState(null);
  const previewAudioRef = useRef(null);

  const stopPreview = useCallback(() => {
    if (previewAudioRef.current) {
      previewAudioRef.current.pause();
      previewAudioRef.current = null;
    }
    setPreviewPlayingId(null);
  }, []);

  const playPreview = useCallback(async (voice) => {
    if (previewPlayingId === voice.id) { stopPreview(); return; }
    stopPreview();
    setPreviewLoadingId(voice.id);
    try {
      const url = `${API}/audiobook/voice-preview/${encodeURIComponent(voice.id)}`;
      const audio = new Audio(url);
      audio.crossOrigin = 'anonymous';
      audio.onended = () => { setPreviewPlayingId(null); previewAudioRef.current = null; };
      audio.onerror = () => {
        toast.error('Preview unavailable — the voice may not have a cached sample yet.');
        setPreviewPlayingId(null); setPreviewLoadingId(null); previewAudioRef.current = null;
      };
      previewAudioRef.current = audio;
      await audio.play();
      setPreviewPlayingId(voice.id);
    } catch {
      toast.error('Could not play preview.');
    } finally {
      setPreviewLoadingId(null);
    }
  }, [previewPlayingId, stopPreview]);

  const loadVoices = useCallback(async () => {
    try {
      const r = await http.get('/admin/audiobook/fish-voices');
      setVoices(r.data?.voices || []);
    } catch {
      setVoices([]);
    }
  }, []);

  useEffect(() => { loadVoices(); }, [loadVoices]);

  const deleteVoice = async (voice) => {
    setDeletingId(voice.id);
    try {
      await http.delete(`/admin/audiobook/fish-voices/${voice.id}`);
      toast.success(`"${voice.title}" deleted and revoked on Fish Audio.`);
      await loadVoices();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not delete this voice.');
    } finally {
      setDeletingId(null);
    }
  };

  const clone = async () => {
    if (!title.trim()) { toast.error('Give the voice a name.'); return; }
    if (!file) { toast.error('Choose a reference audio sample first.'); return; }
    if (file.size > 25 * 1024 * 1024) { toast.error('Sample must be under 25MB.'); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('title', title.trim());
      fd.append('sample', file);
      await http.post('/admin/audiobook/fish-voices', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`"${title.trim()}" cloned — now selectable by authors.`);
      setTitle('');
      setFile(null);
      await loadVoices();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Voice cloning failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-3xl space-y-4" data-testid="audiobook-voices-panel">
      <div>
        <h2 className="flex items-center gap-2 text-[14px] font-semibold">
          <Headphones className="h-4 w-4 text-primary" /> Audiobook Cloned Voices
        </h2>
        <p className="mt-1 text-[12px] text-muted-foreground">
          Upload a clean ~30–60s reference sample (one speaker, minimal background noise) to clone a
          narrator voice via Fish Audio. Cloned voices appear under "Cloned Voice" in every author's
          Generate Audiobook dialog — gated behind the <code>audiobook_voice_cloning</code> capability
          (Premier Studio tier).
        </p>
      </div>

      <div className="rounded-md border border-border bg-card p-4" data-testid="audiobook-voice-upload-form">
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block">
            <span className="mb-1 block text-[11px] uppercase tracking-wider text-muted-foreground">Voice name</span>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Warm British Narrator"
              data-testid="audiobook-voice-title-input"
              className="h-9 w-full rounded-sm border border-border bg-background px-2 text-[12.5px] outline-none focus:border-primary"
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-[11px] uppercase tracking-wider text-muted-foreground">Reference sample (audio, &lt;25MB)</span>
            <input
              type="file"
              accept="audio/*"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid="audiobook-voice-file-input"
              className="block h-9 w-full rounded-sm border border-border bg-background px-2 pt-1.5 text-[11.5px] outline-none focus:border-primary"
            />
          </label>
        </div>
        <button
          type="button"
          onClick={clone}
          disabled={busy}
          data-testid="audiobook-voice-clone-btn"
          className="mt-3 inline-flex items-center gap-1.5 rounded-sm bg-primary px-3 py-1.5 text-[12px] font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
          {busy ? 'Cloning…' : 'Clone voice'}
        </button>
      </div>

      <section>
        <h3 className="mb-2 text-[11px] uppercase tracking-wider text-muted-foreground">
          Cloned library {voices ? `(${voices.length})` : ''}
        </h3>
        {voices === null ? (
          <p className="text-[12px] text-muted-foreground">Loading…</p>
        ) : voices.length === 0 ? (
          <div className="rounded-md border border-dashed border-border bg-card/40 p-6 text-center text-[12px] text-muted-foreground" data-testid="audiobook-voices-empty">
            No cloned voices yet. Upload a reference sample above to add the first one.
          </div>
        ) : (
          <div className="space-y-1.5" data-testid="audiobook-voices-list">
            {voices.map((v) => (
              <div
                key={v.id}
                className="flex items-center justify-between gap-2 rounded-sm border border-border bg-card px-3 py-2 text-[12px]"
                data-testid={`audiobook-voice-row-${v.id}`}
              >
                <span className="inline-flex items-center gap-1.5 font-medium"><Mic className="h-3.5 w-3.5 text-muted-foreground" /> {v.title}</span>
                <div className="flex items-center gap-2">
                  <span className="text-[10.5px] text-muted-foreground">{v.created_at ? new Date(v.created_at).toLocaleString() : ''}</span>
                  {/* Preview button */}
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={previewLoadingId === v.id}
                    onClick={() => playPreview(v)}
                    data-testid={`audiobook-voice-preview-btn-${v.id}`}
                    title={previewPlayingId === v.id ? 'Stop preview' : 'Play voice sample'}
                    className={`h-6 gap-1 px-2 text-[11px] ${previewPlayingId === v.id ? 'border-rose-300 text-rose-600 hover:bg-rose-50' : 'text-primary hover:border-primary/50'}`}
                  >
                    {previewLoadingId === v.id ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : previewPlayingId === v.id ? (
                      <><Square className="h-3 w-3" /> Stop</>
                    ) : (
                      <><Play className="h-3 w-3" /> Preview</>
                    )}
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        disabled={deletingId === v.id}
                        className="h-6 gap-1 px-1.5 text-[11px] text-destructive hover:text-destructive"
                        data-testid={`audiobook-voice-delete-btn-${v.id}`}
                      >
                        {deletingId === v.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent data-testid={`audiobook-voice-delete-dialog-${v.id}`}>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete "{v.title}"?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This revokes the voice on Fish Audio and removes it from every author's "Cloned Voice" list.
                          Blocked automatically if it's in use by an in-progress audiobook job. This can't be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel data-testid={`audiobook-voice-delete-cancel-${v.id}`}>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => deleteVoice(v)}
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          data-testid={`audiobook-voice-delete-confirm-${v.id}`}
                        >
                          Delete voice
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
